<template>
  <div class="predicciones-ia">
    <h3 class="text-2xl font-bold text-gray-800 mb-6">🔮 Predicciones de Inteligencia Artificial</h3>
    
    <!-- Predicción de Ventas -->
    <div v-if="ventasSiguienteMes" class="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-2xl p-8 text-white mb-6">
      <div class="flex items-center gap-3 mb-6">
        <span class="text-5xl">💰</span>
        <div>
          <h4 class="text-2xl font-black">Predicción de Ventas</h4>
          <p class="text-blue-100">Siguiente mes</p>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div class="bg-white bg-opacity-20 rounded-xl p-6 backdrop-blur-sm">
          <div class="text-sm font-semibold mb-2 opacity-90">Escenario Optimista</div>
          <div class="text-3xl font-black">${{ formatNumber(ventasSiguienteMes.escenarios.optimista) }}</div>
        </div>
        <div class="bg-white bg-opacity-30 rounded-xl p-6 backdrop-blur-sm border-2 border-white">
          <div class="text-sm font-semibold mb-2">Escenario Realista</div>
          <div class="text-4xl font-black">${{ formatNumber(ventasSiguienteMes.escenarios.realista) }}</div>
          <div class="text-xs mt-2 opacity-90">Confianza: {{ ventasSiguienteMes.confianza }}%</div>
        </div>
        <div class="bg-white bg-opacity-20 rounded-xl p-6 backdrop-blur-sm">
          <div class="text-sm font-semibold mb-2 opacity-90">Escenario Pesimista</div>
          <div class="text-3xl font-black">${{ formatNumber(ventasSiguienteMes.escenarios.pesimista) }}</div>
        </div>
      </div>

      <div class="bg-white bg-opacity-10 rounded-xl p-4 backdrop-blur-sm">
        <div class="text-sm font-semibold mb-2">🎯 Factores Clave</div>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="(factor, i) in ventasSiguienteMes.factores_clave"
            :key="i"
            class="px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm"
          >
            {{ formatTag(factor) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Productos en Demanda -->
    <div v-if="productosDemanda && productosDemanda.length > 0" class="mb-6">
      <h4 class="text-xl font-bold text-gray-800 mb-4">📦 Productos con Mayor Demanda Predicha</h4>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="(producto, index) in productosDemanda"
          :key="index"
          class="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all"
        >
          <div class="flex items-start justify-between mb-4">
            <div>
              <h5 class="text-lg font-bold text-gray-900 mb-1">{{ producto.producto }}</h5>
              <div class="text-3xl font-black text-blue-600">{{ producto.demanda_estimada }}</div>
              <div class="text-sm text-gray-500">unidades estimadas</div>
            </div>
            <div class="text-right">
              <div class="text-2xl font-bold text-green-600">{{ producto.confianza }}%</div>
              <div class="text-xs text-gray-500">confianza</div>
            </div>
          </div>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="(factor, i) in producto.factores"
              :key="i"
              class="px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold"
            >
              {{ formatTag(factor) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Riesgo de Retrasos -->
    <div v-if="riesgoRetrasos" class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <div :class="[
        'rounded-2xl p-6 border-2',
        riesgoRetrasos.probabilidad > 30 ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'
      ]">
        <div class="flex items-center gap-3 mb-4">
          <span class="text-4xl">⚠️</span>
          <div>
            <h4 class="text-xl font-bold text-gray-900">Riesgo de Retrasos</h4>
            <p class="text-sm text-gray-600">Análisis predictivo</p>
          </div>
        </div>
        <div class="mb-4">
          <div class="text-4xl font-black" :class="riesgoRetrasos.probabilidad > 30 ? 'text-red-600' : 'text-yellow-600'">
            {{ riesgoRetrasos.probabilidad }}%
          </div>
          <div class="text-sm text-gray-600">Probabilidad de retrasos</div>
        </div>
        <div class="bg-white rounded-xl p-4">
          <div class="text-sm font-semibold text-gray-700 mb-2">Productos en riesgo: {{ riesgoRetrasos.productos_riesgo }}</div>
          <div class="text-sm text-gray-600">💡 {{ riesgoRetrasos.recomendacion }}</div>
        </div>
      </div>

      <div v-if="capacidadProduccion" class="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
        <div class="flex items-center gap-3 mb-4">
          <span class="text-4xl">🏭</span>
          <div>
            <h4 class="text-xl font-bold text-gray-900">Capacidad de Producción</h4>
            <p class="text-sm text-gray-600">Análisis de demanda vs capacidad</p>
          </div>
        </div>
        <div class="space-y-3">
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-600">Demanda Estimada</span>
            <span class="text-2xl font-bold text-purple-600">{{ capacidadProduccion.demanda_estimada }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-600">Capacidad Actual</span>
            <span class="text-2xl font-bold text-blue-600">{{ capacidadProduccion.capacidad_actual }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-600">Déficit</span>
            <span class="text-2xl font-bold text-red-600">{{ capacidadProduccion.deficit_estimado }}</span>
          </div>
        </div>
        <div class="mt-4 bg-white rounded-xl p-3">
          <div class="text-xs font-semibold text-gray-700 mb-1">💡 Recomendación</div>
          <div class="text-sm text-gray-600">{{ capacidadProduccion.recomendacion }}</div>
        </div>
      </div>
    </div>

    <!-- Oportunidades de Crecimiento -->
    <div v-if="oportunidades && oportunidades.length > 0">
      <h4 class="text-xl font-bold text-gray-800 mb-4">🚀 Oportunidades de Crecimiento</h4>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="(oportunidad, index) in oportunidades"
          :key="index"
          class="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 hover:shadow-lg transition-all"
        >
          <h5 class="text-lg font-bold text-gray-900 mb-2">{{ oportunidad.area }}</h5>
          <div class="text-3xl font-black text-green-600 mb-3">{{ oportunidad.potencial_crecimiento }}</div>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">Inversión:</span>
              <span class="font-semibold text-gray-900">{{ oportunidad.inversion_requerida }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Tiempo:</span>
              <span class="font-semibold text-gray-900">{{ oportunidad.tiempo_implementacion }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PrediccionesIA',
  props: {
    predicciones: {
      type: Object,
      required: true
    }
  },
  computed: {
    ventasSiguienteMes() {
      return this.predicciones?.ventas_siguiente_mes || null
    },
    productosDemanda() {
      return this.predicciones?.productos_demanda || []
    },
    riesgoRetrasos() {
      return this.predicciones?.riesgo_retrasos || null
    },
    capacidadProduccion() {
      return this.predicciones?.capacidad_produccion || null
    },
    oportunidades() {
      return this.predicciones?.oportunidades_crecimiento || []
    }
  },
  methods: {
    formatNumber(num) {
      if (!num) return '0'
      return new Intl.NumberFormat('es-MX').format(num)
    },
    formatTag(tag) {
      return tag.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }
}
</script>
